import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { t } from '@insight/toolkit-utils/lib/labels';
import KEY_CODES from '@insight/toolkit-utils/lib/types/keyCodes';
import HeaderContext from './HeaderContext';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import HeaderSearchSuggestions from './HeaderSearchSuggestions';
import useOutsideClick from './useOutsideClick';

const HeaderSearch = (props) => {
  const { isSearchActive, setSearchActive } = useContext(HeaderContext);
  const [searchString, setSearchString] = useState('');
  const searchRef = useRef(null);
  const searchClasses = cn(
    'c-header__search',
    { 'is-open': isSearchActive },
    props.className
  );
  const { placeholder } = props;

  /**
   * When the user focuses the input (either for the first time, or to amend
   * the searchh query) trigger the on change handler, giving the app chance
   * to display something (such as history) when there is no query.
   */
  const handleFocus = () => {
    setSearchActive(true);
    props.onChange(searchString);
  };

  /**
   * When the search query changes, track it locally, and inform the parent
   * component of the new value.
   */
  const handleChange = ({ target: { value } }) => {
    setSearchString(value);
    props.onChange(value);
  };

  /**
   * When the submit button is clicked, inform the parent component.
   */
  const handleSubmit = () => {
    props.onSubmit(searchString);
  };

  /**
   * Capture 'Enter' key presses when the input has focus, and submit the
   * search request.
   */
  const handleKeyUp = (event) => {
    if (event.keyCode === KEY_CODES.ENTER && searchString) {
      event.stopPropagation();
      handleSubmit();
    }
  };

  /**
   * Close the search bar when the user click outside, or tabs outside of the
   * search input or the suggestions.
   *
   * Note: Because the button to open the mobile search bar is outside of the
   * search bar itself, useOutsideClick has a strange effect: clicking on the
   * button triggers the outside click behaviour, closing the menu, then the
   * click handler of the button determines that the menu should be reopened.
   * Using setTimeout this way, whilst not ideal, forces the correct order of
   * the handlers to acheive the desired effect. This will have to suffice
   * until we have time to look into how useOutsideClick could be made robust
   * enough to cope. (TODO)
   */
  useOutsideClick(
    () => {
      if (isSearchActive) {
        setTimeout(() => setSearchActive(false), 0);
      }
    },
    { ref: searchRef }
  );

  return (
    <div className={searchClasses}>
      <div
        ref={searchRef}
        className={`c-header-search  ${isSearchActive ? 'is-open' : ''}`}
      >
        <form
          className="c-form  c-form--small"
          onSubmit={(e) => {
            e.preventDefault();
          }}
          role="search"
        >
          <div className="c-form__element">
            <div className="c-form__control">
              <div className="o-grid">
                <div className="o-grid__item">
                  <input
                    aria-label={t('Search')}
                    autoComplete="off"
                    className="c-input  c-header-search__input"
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onKeyUp={handleKeyUp}
                    placeholder={placeholder}
                    type="search"
                    value={searchString}
                  />
                </div>
                <div className="o-grid__item  o-grid__item--shrink">
                  <Button
                    aria-label={t('Search')}
                    className="c-header-search__button"
                    color="subtle"
                    id="searchbutton"
                    onClick={searchString ? handleSubmit : handleFocus}
                  >
                    <Icon icon="search" title={props.title} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
        {props.children}
      </div>
    </div>
  );
};

export default HeaderSearch;

HeaderSearch.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

HeaderSearch.defaultProps = {
  children: null,
  className: '',
  onChange: () => {},
  placeholder: 'What can we help you find?',
};

HeaderSearch.Suggestions = HeaderSearchSuggestions;
